import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Instagram, Star, ChevronLeft, ChevronRight, CheckCircle, MessageCircle, MapPin, Clock } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = 'https://customer-assets.emergentagent.com/job_womens-wellness-72/artifacts/dyehgy3v_Logo.jpg';
const STUDIO = 'https://customer-assets.emergentagent.com/job_womens-wellness-72/artifacts/66wkwhvo_PHOTO-2026-03-29-20-09-08%20%282%29.jpg';
const WA_URL = 'https://wa.me/919920546241?text=Hi%20Meena!%20I%27m%20interested%20in%20joining%20your%20fitness%20class.%20Please%20share%20the%20details.';
const INSTA_URL = 'https://www.instagram.com/meenas_fitness_forever?igsh=OWtkaTN0MTdqN2V0';
const PHONE = '+91 99205 46241';
const LOCATION = '1813, Solus, Hiranandani Estate, Thane';

const FEE_PLANS = [
  { months: 1, amount: '₹2,000', popular: false, saving: null },
  { months: 4, amount: '₹7,500', popular: true, saving: 'Save ₹500' },
  { months: 6, amount: '₹11,000', popular: false, saving: 'Save ₹1,000' },
];

const BENEFITS = [
  'Personalized workout plans',
  'Hybrid mode of classes',
  'Small batches — personal attention',
  'Progress tracking & goal setting',
  'Supportive women-only community',
  'Flexible batch timings',
];

const MOTIVATIONAL = [
  { quote: 'Every rep brings you closer to your best self.', sub: 'Start today.' },
  { quote: 'Strong women inspire strong communities.', sub: 'Join ours.' },
  { quote: 'Every day is a new chance to get stronger.', sub: "Don't wait." },
];

// ── Helper: Format time 07:00 → 7:00 AM ─────────────────────────────────────
function fmt12(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h, 10);
  return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`;
}

// ── Stars ────────────────────────────────────────────────────────────────────
function Stars({ count }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <Star key={i} className="w-4 h-4 text-[#F59E0B]" fill="#F59E0B" strokeWidth={0} />
      ))}
    </div>
  );
}

// ── Motivational Divider ─────────────────────────────────────────────────────
function MotivDivider({ quote, sub }) {
  return (
    <div className="py-12 px-6 text-center bg-gradient-to-r from-[#FFFAF0] via-[#FFEDD5]/60 to-[#FFFAF0]">
      <div className="max-w-xl mx-auto">
        <p className="font-heading text-xl sm:text-2xl font-medium text-[#292524]">"{quote}"</p>
        <p className="text-[#F59E0B] text-sm font-body font-semibold mt-2 uppercase tracking-widest">{sub}</p>
      </div>
    </div>
  );
}

// ── WhatsApp Button ──────────────────────────────────────────────────────────
function WAButton({ size = 'default', label = 'Join Now' }) {
  const base = 'inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5e] text-white font-heading font-semibold rounded-full transition-all duration-300 hover:-translate-y-0.5 shadow-lg hover:shadow-xl';
  const sizes = {
    default: 'px-8 py-3.5 text-base',
    large: 'px-10 py-4 text-lg',
    sm: 'px-6 py-2.5 text-sm',
  };
  return (
    <a href={WA_URL} target="_blank" rel="noopener noreferrer"
      data-testid="whatsapp-cta"
      className={`${base} ${sizes[size]}`}>
      <MessageCircle className="w-5 h-5" strokeWidth={2} />
      {label}
    </a>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function VisitorPortal() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [slide, setSlide] = useState(0);
  const [batches, setBatches] = useState([]);
  const [showcase, setShowcase] = useState(null);
  const galleryRef = useRef(null);
  const galleryIdxRef = useRef(0);   // tracks current scroll index
  const isPausedRef = useRef(false); // paused on hover/interaction
  const autoTimerRef = useRef(null);
  const resumeTimerRef = useRef(null);

  // Scroll-aware navbar
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fetch public batch timings + showcase data
  useEffect(() => {
    axios.get(`${API}/api/public/batches`).then(r => setBatches(r.data)).catch(() => {});
    axios.get(`${API}/api/public/showcase`).then(r => setShowcase(r.data)).catch(() => {});
  }, []);

  // Dynamic gallery: admin-uploaded only, no static fallback
  const galleryImages = showcase?.images?.length > 0
    ? showcase.images.map(i => `${API}${i.url}`)
    : [];

  // Dynamic testimonials: use admin-created only, no fallback
  const testimonials = showcase?.testimonials?.length > 0
    ? showcase.testimonials
    : [];

  // Auto-advance testimonials
  useEffect(() => {
    const id = setInterval(() => setSlide(s => (s + 1) % testimonials.length), 5000);
    return () => clearInterval(id);
  }, [testimonials.length]);

  const prevSlide = () => setSlide(s => (s - 1 + testimonials.length) % testimonials.length);
  const nextSlide = () => setSlide(s => (s + 1) % testimonials.length);

  // Gallery scroll buttons
  const CARD_W = 296; // 280px card + 16px gap-4

  // Append a clone of the first image so the loop is seamless (no visible jump back)
  const displayImages = galleryImages.length > 1
    ? [...galleryImages, galleryImages[0]]
    : galleryImages;

  // Pause auto-scroll; resume automatically after 3 s of inactivity
  const pauseGallery = () => {
    isPausedRef.current = true;
    clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => { isPausedRef.current = false; }, 3000);
  };

  // Arrow / manual navigation
  const scrollGallery = (dir) => {
    if (!galleryRef.current) return;
    pauseGallery();
    galleryRef.current.scrollBy({ left: dir * CARD_W, behavior: 'smooth' });
    galleryIdxRef.current = Math.max(0, galleryIdxRef.current + dir);
  };

  // Auto-scroll: advance one card every 3 s with infinite loop
  useEffect(() => {
    if (galleryImages.length <= 1) return;
    const total = galleryImages.length + 1; // originals + 1 clone
    const advance = () => {
      if (isPausedRef.current || !galleryRef.current) return;
      const nextIdx = galleryIdxRef.current + 1;
      galleryRef.current.scrollBy({ left: CARD_W, behavior: 'smooth' });
      galleryIdxRef.current = nextIdx;
      if (nextIdx >= total - 1) {
        // Just scrolled to the clone — silently reset to index 0 after animation
        setTimeout(() => {
          if (galleryRef.current) {
            galleryRef.current.scrollTo({ left: 0, behavior: 'instant' });
            galleryIdxRef.current = 0;
          }
        }, 700);
      }
    };
    autoTimerRef.current = setInterval(advance, 3000);
    return () => {
      clearInterval(autoTimerRef.current);
      clearTimeout(resumeTimerRef.current);
    };
  }, [galleryImages.length]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-[#FFFAF0] font-body">

      {/* ── STICKY NAVBAR ──────────────────────────────────────────── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300
        ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-[#E7E5E4]/60' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <img src="https://customer-assets.emergentagent.com/job_womens-wellness-72/artifacts/mi9gwt69_image.png" alt="Meena's Fitness Forever" className="h-9 object-contain" />
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/login')}
              data-testid="visitor-login-button"
              className={`text-sm font-heading font-medium transition-colors ${scrolled ? 'text-[#57534E] hover:text-[#D97706]' : 'text-white/80 hover:text-white'}`}>
              Login
            </button>
            <WAButton size="sm" label="Join Now" />
          </div>
        </div>
      </nav>

      {/* ── SECTION 1: HERO ────────────────────────────────────────── */}
      <section
        data-testid="visitor-hero-section"
        className="relative min-h-screen flex flex-col items-center justify-center text-center pt-16 overflow-hidden"
        style={{ backgroundImage: `url(${STUDIO})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/75" />
        <div className="absolute inset-0 backdrop-blur-[1px]" />

        <div className="relative z-10 px-6 py-20 max-w-3xl mx-auto animate-fade-in-up">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-3xl p-3 shadow-2xl">
              <img src={LOGO} alt="Meena's Fitness Forever" className="h-16 sm:h-20 object-contain" style={{ mixBlendMode: 'multiply' }} />
            </div>
          </div>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 text-white rounded-full px-5 py-2 text-xs font-body mb-6">
            <span className="w-2 h-2 bg-[#F59E0B] rounded-full animate-pulse" />
            Women's Fitness Community · Mumbai
          </div>

          {/* Title */}
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
            Meena's<br />
            <span className="text-[#F59E0B]">Fitness Forever</span>
          </h1>

          {/* Tagline */}
          <p className="text-white/85 text-lg sm:text-xl font-body max-w-xl mx-auto mb-10 leading-relaxed">
            Transform your body. Strengthen your mind.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <WAButton size="large" label="Join Now" />
            <a href="#about"
              className="inline-flex items-center gap-2 border-2 border-white/50 hover:border-white text-white rounded-full px-8 py-4 font-heading font-semibold text-base hover:-translate-y-0.5 transition-all duration-300">
              Learn More
            </a>
          </div>

          {/* Scroll hint */}
          <div className="mt-16 flex justify-center animate-bounce">
            <ChevronRight className="w-6 h-6 text-white/50 rotate-90" strokeWidth={1.5} />
          </div>
        </div>
      </section>

      {/* ── MOTIVATIONAL 1 ───────────────────────────────────────────── */}
      <MotivDivider {...MOTIVATIONAL[0]} />

      {/* ── SECTION 2: ABOUT ─────────────────────────────────────────── */}
      <section id="about" data-testid="visitor-about-section" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* Text */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-3">About Us</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#292524] mb-5 leading-tight">
                Fitness That Feels Like Family
              </h2>
              <p className="text-[#57534E] font-body leading-relaxed mb-4 text-base">
                Meena's Fitness Forever is more than just a class — it's a community built for women, by a woman who truly cares. Founded by <strong className="text-[#292524]">Meena</strong>, a dedicated fitness coach with a decade of hands-on experience, our program blends strength training, yoga, cardio, aerobics and nutrition guidance into one transformative journey.
              </p>
              <p className="text-[#57534E] font-body leading-relaxed mb-7 text-base">
                Whether you're a complete beginner or a seasoned fitness enthusiast, Meena meets you exactly where you are — with warmth, expertise, and the kind of personal attention that drives real results.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {BENEFITS.map(b => (
                  <div key={b} className="flex items-center gap-2.5">
                    <CheckCircle className="w-4 h-4 text-[#F59E0B] shrink-0" strokeWidth={2} />
                    <span className="text-sm text-[#292524] font-body">{b}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Image grid */}
              <div className="grid grid-cols-2 gap-4">
              <div className="rounded-3xl overflow-hidden shadow-card row-span-2" style={{ height: '360px' }}>
                <img src={galleryImages[1] || STUDIO} alt="fitness" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="rounded-3xl overflow-hidden shadow-card" style={{ height: '170px' }}>
                <img src={galleryImages[2] || STUDIO} alt="fitness" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="rounded-3xl overflow-hidden shadow-card" style={{ height: '170px' }}>
                <img src={galleryImages[3] || STUDIO} alt="fitness" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MOTIVATIONAL 2 ───────────────────────────────────────────── */}
      <MotivDivider {...MOTIVATIONAL[1]} />

      {/* ── SECTION 3: GALLERY ───────────────────────────────────────── */}
      <section data-testid="visitor-gallery-section" className="py-20 bg-[#FFFAF0] overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center px-6 mb-8">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-2">Gallery</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#292524]">Life at Meena's Fitness Forever</h2>
            <p className="text-[#57534E] text-base font-body mt-2">A glimpse into our vibrant, energetic community</p>
          </div>

          {/* Carousel with arrows */}
          <div className="relative">
            {/* Left arrow */}
            <button onClick={() => scrollGallery(-1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/90 hover:bg-white shadow-lg rounded-full flex items-center justify-center text-[#292524] hover:text-[#F59E0B] transition-all">
              <ChevronLeft className="w-5 h-5" strokeWidth={2} />
            </button>

            {/* Scrollable gallery or empty state */}
            {galleryImages.length === 0 ? (
              <div className="flex items-center justify-center h-40 text-[#A8A29E] font-body text-base italic px-6">
                Gallery will be updated soon.
              </div>
            ) : (
              <div
                ref={galleryRef}
                className="flex gap-4 overflow-x-auto scrollbar-hide px-6 sm:px-10 pb-4 snap-x snap-mandatory"
                style={{ scrollPaddingLeft: '24px' }}
                onMouseEnter={pauseGallery}
                onMouseLeave={() => { isPausedRef.current = false; }}
                onTouchStart={pauseGallery}
              >
                {displayImages.map((src, i) => (
                  <div
                    key={i}
                    data-testid={`gallery-image-${i}`}
                    className="snap-start shrink-0 rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover transition-shadow duration-300"
                    style={{ width: '280px', height: '340px' }}
                  >
                    <img
                      src={src}
                      alt={`gallery ${i + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Right arrow */}
            <button onClick={() => scrollGallery(1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/90 hover:bg-white shadow-lg rounded-full flex items-center justify-center text-[#292524] hover:text-[#F59E0B] transition-all">
              <ChevronRight className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>
        </div>
      </section>

      {/* ── SECTION 4: TESTIMONIALS ──────────────────────────────────── */}
      <section data-testid="visitor-testimonials-section" className="py-20 px-6 bg-[#292524]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-2">Stories</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">What Our Members Say</h2>
            <p className="text-white/60 text-base font-body mt-2">Real transformations from real women</p>
          </div>

          {/* Slider — fixed-height cards with internal scroll */}
          {testimonials.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-white/50 font-body text-base italic">
              Testimonials coming soon.
            </div>
          ) : (
            <>
              <div className="relative overflow-hidden rounded-3xl">
                <div
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${slide * 100}%)` }}
                >
                  {testimonials.map((t, i) => (
                    <div key={i} data-testid={`testimonial-card-${i}`} className="w-full shrink-0 px-1">
                      {/* Fixed-height card: name (top) → stars → review (scrollable) */}
                      <div
                        className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-3xl px-7 pt-6 pb-5"
                        style={{ height: '210px', display: 'flex', flexDirection: 'column' }}
                      >
                        {/* Name */}
                        <p className="font-heading font-semibold text-white text-base shrink-0 mb-1.5">{t.name}</p>
                        {/* Stars */}
                        <div className="shrink-0 mb-3">
                          <Stars count={t.rating} />
                        </div>
                        {/* Scrollable review */}
                        <div
                          className="flex-1 overflow-y-auto pr-1"
                          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.12) transparent' }}
                        >
                          <p className="text-white/85 font-body text-sm leading-relaxed italic">"{t.text}"</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-4 mt-6">
                <button onClick={prevSlide} data-testid="testimonial-prev"
                  className="w-10 h-10 bg-white/10 hover:bg-[#F59E0B] rounded-full flex items-center justify-center text-white transition-all">
                  <ChevronLeft className="w-4 h-4" strokeWidth={2} />
                </button>
                <div className="flex gap-2">
                  {testimonials.map((_, i) => (
                    <button key={i} onClick={() => setSlide(i)}
                      className={`rounded-full transition-all duration-300 ${i === slide ? 'w-6 h-2 bg-[#F59E0B]' : 'w-2 h-2 bg-white/30 hover:bg-white/60'}`} />
                  ))}
                </div>
                <button onClick={nextSlide} data-testid="testimonial-next"
                  className="w-10 h-10 bg-white/10 hover:bg-[#F59E0B] rounded-full flex items-center justify-center text-white transition-all">
                  <ChevronRight className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ── MOTIVATIONAL 3 ───────────────────────────────────────────── */}
      <MotivDivider {...MOTIVATIONAL[2]} />

      {/* ── SECTION 5: CLASS DETAILS ─────────────────────────────────── */}
      <section data-testid="visitor-class-details-section" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-2">Class Details</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#292524]">Everything You Need to Know</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* Fee Plans */}
            <div>
              <h3 className="font-heading text-xl font-semibold text-[#292524] mb-5">Fee Plans</h3>
              <div className="space-y-4">
                {FEE_PLANS.map(plan => (
                  <div key={plan.months}
                    data-testid={`fee-plan-${plan.months}`}
                    className={`relative flex items-center justify-between rounded-2xl p-5 border-2 transition-all
                      ${plan.popular
                        ? 'border-[#F59E0B] bg-[#FFFAF0] shadow-button'
                        : 'border-[#E7E5E4] bg-white hover:border-[#F59E0B]/50'
                      }`}>
                    {plan.popular && (
                      <span className="absolute -top-3 left-4 bg-[#F59E0B] text-white text-xs font-heading font-semibold rounded-full px-3 py-0.5">
                        Most Popular
                      </span>
                    )}
                    <div>
                      <p className="font-heading font-bold text-[#292524] text-lg">{plan.amount}</p>
                      <p className="text-[#57534E] font-body text-sm">{plan.months} {plan.months === 1 ? 'Month' : 'Months'}</p>
                    </div>
                    <div className="text-right">
                      {plan.saving && (
                        <span className="text-xs text-green-600 font-body font-semibold bg-green-50 rounded-full px-3 py-1">{plan.saving}</span>
                      )}
                      <a href={WA_URL} target="_blank" rel="noopener noreferrer"
                        className="block mt-2 text-[#F59E0B] hover:text-[#D97706] text-sm font-heading font-semibold hover:underline">
                        Enquire →
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Batch timings + Location */}
            <div className="space-y-6">
              {/* Batch timings */}
              <div className="bg-[#FFFAF0] rounded-3xl p-6 border border-[#E7E5E4]">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-[#F59E0B]" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg font-semibold text-[#292524]">Batch Timings</h3>
                </div>
                {batches.length > 0 ? (
                  <div className="space-y-3">
                    {batches.map((b, i) => (
                      <div key={i} data-testid={`batch-timing-${i}`}
                        className="flex items-center justify-between bg-white rounded-2xl px-4 py-3 border border-[#E7E5E4]/60">
                        <p className="font-heading font-medium text-[#292524] text-sm">{b.name}</p>
                        <p className="text-[#F59E0B] font-body text-sm font-semibold">
                          {fmt12(b.start_time)} – {fmt12(b.end_time)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl px-4 py-3 border border-[#E7E5E4]/60">
                    <p className="text-[#57534E] font-body text-sm">Contact Meena for current batch timings.</p>
                  </div>
                )}
              </div>

              {/* Location & Contact */}
              <div className="bg-[#FFFAF0] rounded-3xl p-6 border border-[#E7E5E4]">
                <h3 className="font-heading text-lg font-semibold text-[#292524] mb-4">Contact & Location</h3>
                <div className="space-y-3">
                  <a href={`tel:${PHONE}`} className="flex items-center gap-3 group">
                    <div className="w-9 h-9 bg-[#FFEDD5] rounded-xl flex items-center justify-center shrink-0 group-hover:bg-[#F59E0B] transition-colors">
                      <Phone className="w-4 h-4 text-[#F59E0B] group-hover:text-white transition-colors" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs text-[#A8A29E] font-body">Call or WhatsApp</p>
                      <p className="text-[#292524] font-body font-medium text-sm">{PHONE}</p>
                    </div>
                  </a>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#FFEDD5] rounded-xl flex items-center justify-center shrink-0">
                      <MapPin className="w-4 h-4 text-[#F59E0B]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs text-[#A8A29E] font-body">Location</p>
                      <p className="text-[#292524] font-body font-medium text-sm">{LOCATION}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 6: SOCIALS ────────────────────────────────────────── */}
      <section data-testid="visitor-socials-section"
        className="py-16 px-6 bg-gradient-to-br from-[#F59E0B] to-[#D97706]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">Follow Our Journey</p>
          <p className="text-white/80 font-body mb-8">Stay connected for class updates, tips, and motivation</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={INSTA_URL} target="_blank" rel="noopener noreferrer"
              data-testid="instagram-link"
              className="inline-flex items-center justify-center gap-2.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white rounded-full px-8 py-3.5 font-heading font-semibold text-base transition-all hover:-translate-y-0.5">
              <Instagram className="w-5 h-5" strokeWidth={1.5} />
              @meenas_fitness_forever
            </a>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer"
              data-testid="whatsapp-socials"
              className="inline-flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#1ebe5e] text-white rounded-full px-8 py-3.5 font-heading font-semibold text-base transition-all hover:-translate-y-0.5 shadow-lg">
              <MessageCircle className="w-5 h-5" strokeWidth={2} />
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* ── SECTION 8: FINAL CTA ─────────────────────────────────────── */}
      <section data-testid="visitor-final-cta" className="py-24 px-6 bg-[#1C1917] text-center relative overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-[#F59E0B]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[#F59E0B]/10 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-2xl mx-auto">
          <div className="flex justify-center mb-8">
            <img src="https://customer-assets.emergentagent.com/job_womens-wellness-72/artifacts/mi9gwt69_image.png" alt="logo" className="h-16 object-contain" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-4">Your Journey Starts Here</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            Ready to begin<br />your transformation?
          </h2>
          <p className="text-white/60 font-body text-base mb-10 max-w-md mx-auto">
            Join hundreds of women who have transformed their lives with Meena's guidance. Your first step is just one message away.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <WAButton size="large" label="Join Now on WhatsApp" />
            <button onClick={() => navigate('/login')}
              data-testid="visitor-login-cta"
              className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white/70 text-white/80 hover:text-white rounded-full px-8 py-4 font-heading font-semibold text-base transition-all hover:-translate-y-0.5">
              Already a member? Login
            </button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────────────────── */}
      <footer className="py-8 px-6 bg-black/95 text-center">
        <img src={LOGO} alt="logo" className="h-8 object-contain mx-auto mb-3 opacity-60" />
        <p className="text-white/40 text-xs font-body">
          &copy; {new Date().getFullYear()} Meena's Fitness Forever · Mumbai, Maharashtra
        </p>
        <p className="text-white/30 text-xs font-body mt-1">
          Made with love for strong women
        </p>
      </footer>

    </div>
  );
}
